import {
  type ColumnWidth,
  PRINT_GRID_LABELS,
  type PrintBody,
  type PrintGrid,
  printGridSchema,
  type PrintTestFlow,
  printTestFlowSchema,
  RESULT_COLUMN_DESCRIPTIONS,
  type ResultColumn,
} from '#shared/types';
import { Checkbox } from '#client/components/ui/Checkbox';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import { ColorField } from '../controls/ColorField';
import { NumberField } from '../controls/NumberField';
import { OrderedList } from '../controls/OrderedList';
import { ALIGNMENT_OPTIONS } from './alignment-options';

interface BodySectionProps {
  body: PrintBody;
  onChange: (patch: Partial<PrintBody>) => void;
}

const TEST_FLOW_LABELS = {
  new_page: 'Each test on a new page',
  same_page: 'Continue on the same page when the whole test fits',
} as const satisfies Record<PrintTestFlow, string>;

export function BodySection({ body, onChange }: BodySectionProps) {
  return (
    <>
      <TitleFields body={body} onChange={onChange} />

      <ColumnList body={body} onChange={onChange} />
      <p className="c-field__help">
        Reference-range columns (unit, range, age, age group, gender, other) are left out of a test
        where no parameter has a value for them.
      </p>

      <SizeFields body={body} onChange={onChange} />

      <RuleFields body={body} onChange={onChange} />

      <FlagFields body={body} onChange={onChange} />

      <NotesFields body={body} onChange={onChange} />

      <FlowFields body={body} onChange={onChange} />
    </>
  );
}

/**
 * The results table's columns: which, in what order, how wide and how aligned.
 *
 * One show/hide checkbox per column, every one on by default. A column from
 * the reference range is also left out of any test where no row has a value
 * for it, so switching it on never prints an empty column.
 *
 * Width is `Fill`, `Fit` or a millimetre figure. `Fill` shares whatever is left
 * over — at least one column should have it, and the renderer gives it to the
 * first column if a template has none, rather than drawing a table that floats
 * against the left margin looking broken.
 */
function ColumnList({ body, onChange }: BodySectionProps) {
  return (
    <OrderedList<ResultColumn>
      items={body.columns}
      onChange={(columns) => onChange({ columns })}
      keyOf={(column) => column.key}
      labelOf={(column) => column.label || RESULT_COLUMN_DESCRIPTIONS[column.key]}
      enabledOf={(column) => column.enabled}
      setEnabled={(column, enabled) => ({ ...column, enabled })}
      detail={(column, replace) => (
        <>
          <TextField
            label={`Heading for ${column.key}`}
            hideLabel
            value={column.label}
            placeholder={`Heading — blank prints none (${RESULT_COLUMN_DESCRIPTIONS[column.key]})`}
            onChange={(event) => replace({ ...column, label: event.target.value })}
          />
          <div className="c-builder__grid">
            <Select
              label="Width"
              value={typeof column.width === 'number' ? 'fixed' : column.width}
              onChange={(choice) => replace({ ...column, width: widthFor(choice, column.width) })}
              options={[
                { value: '*', label: 'Fill' },
                { value: 'auto', label: 'Fit contents' },
                { value: 'fixed', label: 'Fixed' },
              ]}
            />
            {typeof column.width === 'number' && (
              <NumberField
                label="Width"
                unit="mm"
                value={column.width}
                onChange={(width) => replace({ ...column, width })}
                min={5}
                max={200}
              />
            )}
            <Select
              label="Alignment"
              value={column.align}
              onChange={(choice) => replace({ ...column, align: choice })}
              options={ALIGNMENT_OPTIONS}
            />
          </div>
        </>
      )}
    />
  );
}

/** Keeps the last fixed width when switching away and back. */
function widthFor(choice: string, current: ColumnWidth): ColumnWidth {
  if (choice === '*' || choice === 'auto') return choice;
  return typeof current === 'number' ? current : 25;
}

/** Whether and how the test name prints over its table. */
function TitleFields({ body, onChange }: BodySectionProps) {
  return (
    <>
      <Checkbox
        label="Print the test name over its table"
        checked={body.title_enabled}
        onChange={(event) => onChange({ title_enabled: event.target.checked })}
      />
      <p className="c-field__help">
        A test with a single parameter never prints its name twice — the parameter row carries it.
      </p>

      {body.title_enabled && (
        <>
          <Segmented
            label="Test name alignment"
            value={body.title_align}
            onChange={(title_align) => onChange({ title_align })}
            options={ALIGNMENT_OPTIONS}
          />
          <div className="c-builder__grid">
            <NumberField
              label="Test name size"
              unit="pt"
              value={body.title_size}
              onChange={(title_size) => onChange({ title_size })}
              min={6}
              max={36}
              step={0.5}
            />
            <Checkbox
              label="Upper case"
              checked={body.title_uppercase}
              onChange={(event) => onChange({ title_uppercase: event.target.checked })}
            />
          </div>
        </>
      )}
    </>
  );
}

/** Type sizes, and the row padding that sets how many rows reach a page. */
function SizeFields({ body, onChange }: BodySectionProps) {
  return (
    <div className="c-builder__grid">
      <NumberField
        label="Result size"
        unit="pt"
        value={body.size}
        onChange={(size) => onChange({ size })}
        min={5}
        max={16}
        step={0.5}
      />
      <NumberField
        label="Heading size"
        unit="pt"
        value={body.heading_size}
        onChange={(heading_size) => onChange({ heading_size })}
        min={5}
        max={16}
        step={0.5}
      />
      <NumberField
        label="Row padding"
        unit="mm"
        value={body.row_padding}
        onChange={(row_padding) => onChange({ row_padding })}
        min={0}
        max={8}
        step={0.5}
        helpText="Drives how many rows reach a page."
      />
    </div>
  );
}

/** Table rules and alternate-row tint. */
function RuleFields({ body, onChange }: BodySectionProps) {
  return (
    <>
      <Select
        label="Rules"
        value={body.grid}
        onChange={(choice) => onChange({ grid: choice })}
        options={printGridSchema.options.map((grid: PrintGrid) => ({
          value: grid,
          label: PRINT_GRID_LABELS[grid],
        }))}
      />
      <ColorField
        label="Rule colour"
        value={body.grid_color}
        onChange={(grid_color) => onChange({ grid_color })}
      />

      <Checkbox
        label="Tint alternate rows"
        checked={body.zebra}
        onChange={(event) => onChange({ zebra: event.target.checked })}
      />
      {body.zebra && (
        <ColorField
          label="Tint"
          value={body.zebra_color}
          onChange={(zebra_color) => onChange({ zebra_color })}
        />
      )}
    </>
  );
}

/** How out-of-range results are marked. */
function FlagFields({ body, onChange }: BodySectionProps) {
  return (
    <>
      <Checkbox
        label="Mark results outside their reference range"
        checked={body.flag_abnormal}
        onChange={(event) => onChange({ flag_abnormal: event.target.checked })}
      />
      {body.flag_abnormal && (
        <>
          <Checkbox
            label="Set flagged results in bold"
            checked={body.bold_abnormal}
            onChange={(event) => onChange({ bold_abnormal: event.target.checked })}
          />
          <ColorField
            label="Flag colour"
            value={body.abnormal_color}
            onChange={(abnormal_color) => onChange({ abnormal_color })}
            helpText="Flagging always uses the range recorded on the report, never the parameter's current one."
          />
        </>
      )}
    </>
  );
}

/** Remarks and comments under each test. */
function NotesFields({ body, onChange }: BodySectionProps) {
  return (
    <>
      <Checkbox
        label="Print remarks (above comments, no heading)"
        checked={body.remarks.enabled}
        onChange={(event) =>
          onChange({ remarks: { ...body.remarks, enabled: event.target.checked } })
        }
      />

      <Checkbox
        label="Print comments"
        checked={body.comments.enabled}
        onChange={(event) =>
          onChange({ comments: { ...body.comments, enabled: event.target.checked } })
        }
      />
      {body.comments.enabled && (
        <TextField
          label="Comments heading"
          value={body.comments.title}
          onChange={(event) =>
            onChange({ comments: { ...body.comments, title: event.target.value } })
          }
        />
      )}
    </>
  );
}

/** Department headings, and what happens between tests. */
function FlowFields({ body, onChange }: BodySectionProps) {
  return (
    <>
      <Checkbox
        label="Print the department heading"
        checked={body.department_heading}
        onChange={(event) => onChange({ department_heading: event.target.checked })}
      />

      <Select
        label="Between tests"
        value={body.test_flow}
        onChange={(choice) => onChange({ test_flow: choice })}
        options={printTestFlowSchema.options.map((flow: PrintTestFlow) => ({
          value: flow,
          label: TEST_FLOW_LABELS[flow],
        }))}
        helpText="On the same page, a test that does not fit in the space left moves to the next page whole."
      />
    </>
  );
}
